import React, { createRef, useCallback, useEffect, useState } from 'react'
import { Box, Icon, IconButton, LinearProgress, Typography } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxDropzone from 'components/reusable/AdxDropzone'
import { _calculateFileSize } from 'services/helper/FileSize'
import { CSVLink } from 'react-csv'
import AdxButton from 'components/custom/AdxButton'

const ACExternalEvalCsvUpload = ({ formikProps, handleConfirm, enableUpload }) => {
  const { values, setFieldValue, handleSubmit, resetForm } = formikProps
  const [uploadState, setUploadState] = useState('PENDING')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [files, setFiles] = useState([])
  const [errFiles, setErrFiles] = useState([])
  const dropzoneRef = createRef()

  const style = {
    color: '#09A3A9',
    cursor: 'pointer',
    textDecoration: 'underline',
  }

  const CSVHeader = [['Name', 'Email', 'Score']]

  useEffect(() => {
    if (values.import_evaluation_file) {
      handleSubmit()
    }
  }, [values]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (values.import_evaluation_file && values.import_evaluation_file.length !== 0) {
      setUploadState('READY')
      setFiles(values.import_evaluation_file)
    }
  }, [values.import_evaluation_file])

  const openDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }

  const handleUpload = useCallback(async (acceptedFiles, fileRejections) => {
    // This function will be handled later with API call using axios.
    setUploadState('UPLOADING')
    if (fileRejections.length && !acceptedFiles.length) {
      setUploadState('NOT_READY')
      setFiles([])
      setErrFiles(fileRejections.map((file) => file))
    }
    let csvData = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    )
    setFieldValue('import_evaluation_file', csvData)
    setFiles(csvData)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelUpload = () => {
    setUploadState('PENDING')
    setUploadProgress(0)
    setFiles([])
    setErrFiles([])
    resetForm()
    handleConfirm(false)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        rowGap: 2,
        justifyContent: 'center',
      }}
    >
      <Box>
        <Field name="import_evaluation_file" values={values.import_evaluation_file} type="hidden" />
        <Box display="flex" flexDirection="column">
          {uploadState === 'PENDING' ? (
            <Box
              sx={{
                width: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <Typography variant="body1">Upload CSV file</Typography>
                <Typography variant="body2" color="light-gray-text.main">
                  Upload a list of evaluation data via CSV file.{' '}
                  <CSVLink
                    filename={'External_evaluation.csv'}
                    data={CSVHeader}
                    style={{ textDecoration: 'none' }}
                  >
                    <span style={style}>Download template</span>
                  </CSVLink>
                </Typography>
              </Box>
              <Box>
                <Field
                  name="import_participant_file"
                  values={values.import_participant_file}
                  type="hidden"
                />
                <Box display="flex" flexDirection="column" rowGap={1}>
                  <AdxButton
                    variant={'outlined'}
                    onClick={openDialog}
                    sx={{
                      width: '150px',
                      display: 'flex',
                      gap: 1,
                    }}
                    disabled={!enableUpload}
                  >
                    <Icon
                      sx={{
                        color: enableUpload ? 'alternate.main' : '#E5E5E5',
                      }}
                    >
                      publish
                    </Icon>
                    Upload CSV
                  </AdxButton>
                  <AdxDropzone
                    setUploadState={setUploadState}
                    ref={dropzoneRef}
                    useDropzoneObject={{
                      accept: [
                        '.csv',
                        'application/vnd.ms-excel',
                        'text/csv',
                        '.xlsx',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      ],
                      maxSize: 2097152,
                      maxFiles: 1,
                      onDrop: handleUpload,
                    }}
                    sx={{
                      display: 'none',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                minWidth: '250px',
                minHeight: '130px',
                height: '100%',
                width: '100%',
                borderRadius: '4.5px',
                border: '3px solid',
                borderColor: 'gray-line.main',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Ready */}
              {uploadState === 'READY' && (
                <>
                  <Box
                    sx={{
                      backgroundColor: 'gray-background.main',
                      height: '100px',
                      width: '100%',
                      borderRadius: '4.5px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: '3rem',
                        color: 'light-gray-text-2.main',
                      }}
                    >
                      file_copy
                    </Icon>
                  </Box>
                </>
              )}

              {/* Uploading */}
              {uploadState === 'UPLOADING' && (
                <LinearProgress
                  value={uploadProgress}
                  color="alternate"
                  sx={{
                    width: '100%',
                  }}
                />
              )}
              {/* Upload Footer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  p: 2,
                }}
              >
                {/* Not Ready */}
                {uploadState === 'NOT_READY'
                  ? errFiles.map(({ file, errors }, index) => (
                      <>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          columnGap={1}
                          key={index}
                        >
                          <Icon sx={{ color: 'error.main' }}>cancel</Icon>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {file.name} not ready
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="error.main" key={index}>
                          {errors[0].message}
                        </Typography>
                        <IconButton onClick={handleCancelUpload}>
                          <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                        </IconButton>
                      </>
                    ))
                  : files.map((file, index) => (
                      <Box display="flex" flexDirection="column" key={index}>
                        <Box display="flex" columnGap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {file.name}
                          </Typography>
                          <Typography variant="body2">{_calculateFileSize(file.size)}</Typography>
                        </Box>
                        {(
                          <ErrorMessage name="import_evaluation_file" component={AdxErrorMessage} />
                        ) && (
                          <Typography variant="body2">
                            {
                              <ErrorMessage
                                name="import_evaluation_file"
                                component={AdxErrorMessage}
                              />
                            }
                          </Typography>
                        )}
                      </Box>
                    ))}

                {/* Uploading */}
                {uploadState === 'UPLOADING' && (
                  <Box display="flex" alignItems="center" columnGap={1}>
                    <Typography variant="body1" color="light-gray-text.main">
                      {`Uploading (${uploadProgress.toFixed(0)}%)`}
                    </Typography>
                    <IconButton onClick={handleCancelUpload}>
                      <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                    </IconButton>
                  </Box>
                )}

                {/* Ready */}
                {uploadState === 'READY' && (
                  <Box display="flex" alignItems="center" columnGap={1}>
                    <AdxButton variant={'ghost'} size="small" onClick={handleCancelUpload}>
                      Replace
                    </AdxButton>
                    <IconButton onClick={handleCancelUpload}>
                      <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ACExternalEvalCsvUpload
